/**
 * CreditModeToggle: Weighted / Actual / Both in the Vera Sales header. Plain
 * buttons styled for the dark header bar; the AppShell gives the slot no
 * styling of its own.
 */

import { Tooltip, makeStyles, mergeClasses } from '@fluentui/react-components';
import { CREDIT_MODES, CREDIT_MODE_LABELS, type CreditMode } from '@shared/lib/sales-credit';
import { useCreditMode } from './CreditModeProvider';

const HINTS: Record<CreditMode, string> = {
  weighted: 'Lives and groups at the New Business weights: what counts toward targets. Open deals are provisional until they close.',
  actual: 'Sales Hub counts as they are, no weights.',
  both: 'Actual with weighted beside it.',
};

const useStyles = makeStyles({
  group: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginRight: '8px',
  },
  btn: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: 'rgba(255,255,255,0.85)',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '24px',
    padding: '0 10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    ':hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
    ':focus-visible': { outline: '2px solid #fff', outlineOffset: '-2px' },
  },
  btnOn: {
    backgroundColor: 'rgba(255,255,255,0.22)',
    color: '#fff',
    ':hover': { backgroundColor: 'rgba(255,255,255,0.26)' },
  },
});

export function CreditModeToggle() {
  const styles = useStyles();
  const { mode, setMode } = useCreditMode();
  return (
    <div className={styles.group} role="radiogroup" aria-label="Credit reading: weighted, actual or both">
      {CREDIT_MODES.map((m) => (
        <Tooltip key={m} relationship="description" content={HINTS[m]}>
          <button
            type="button"
            role="radio"
            aria-checked={mode === m}
            className={mergeClasses(styles.btn, mode === m && styles.btnOn)}
            onClick={() => setMode(m)}
          >
            {CREDIT_MODE_LABELS[m]}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
